import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Target, Users, MapPin, Star, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const AboutPage = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To connect every film professional in India with the right opportunities and create a thriving ecosystem for the film industry.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Built by film professionals, for film professionals. We understand your challenges because we\'ve lived them.'
    },
    {
      icon: Star,
      title: 'Quality Matters',
      description: 'Every professional on our platform is verified, ensuring you connect with genuine, skilled crew members.'
    }
  ];

  const differentiators = [
    'Verified professionals with portfolio reviews',
    'Location-based search across 100+ Indian cities',
    '15+ specialized department categories',
    'Direct connection between clients and crew',
    'Transparent ratings and reviews',
    'Mobile-friendly platform for on-the-go access'
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
              About Us
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Connecting India's Film Industry,
              <span className="text-accent"> One Crew at a Time</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              We're building the largest, most trusted network of film professionals in India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6 text-foreground">The Problem We Solve</h2>
              <Card className="bg-card border-border">
                <CardContent className="p-8">
                  <p className="text-muted-foreground text-base leading-relaxed mb-4">
                    Finding the right film crew in India has always been challenging. Production houses rely on word-of-mouth, 
                    making it difficult to discover new talent. Skilled professionals struggle to get noticed beyond their immediate network. 
                    The process is fragmented, time-consuming, and often leaves both parties frustrated.
                  </p>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    <strong className="text-foreground">Shoot Base changes that.</strong> We bring transparency, accessibility, and trust to film hiring. 
                    Whether you're a cinematographer in Chennai or a production house in Mumbai, we make meaningful connections happen.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Our Story */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 text-foreground">Our Story</h2>
              <Card className="bg-gradient-to-br from-accent/5 to-background border-accent/20">
                <CardContent className="p-8">
                  <p className="text-muted-foreground text-base leading-relaxed mb-4">
                    Shoot Base was born from firsthand experience. Our founders spent years working in the Indian film industry, 
                    witnessing talented professionals struggle to find work and producers waste weeks hunting for the right crew.
                  </p>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    We realized the industry needed a dedicated platform—one that understands the unique needs of film production, 
                    respects the craft of professionals, and bridges the gap with technology. Today, we're proud to serve 
                    thousands of film professionals and production houses across India.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-foreground">What Drives Us</h2>
            <p className="text-muted-foreground text-base">The values that guide everything we do</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full bg-card border-border hover:border-accent/50 transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="bg-accent/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                      <value.icon className="w-7 h-7 text-accent" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">{value.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-8 text-foreground text-center">What Makes Shoot Base Different</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {differentiators.map((item, index) => (
                  <motion.div
                    key={`diff-${item.slice(0, 20)}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">{item}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">Ready to Join Our Community?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Whether you're looking for talent or looking for work, Shoot Base is here for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-primary-foreground font-semibold group"
                onClick={() => navigate('/create-profile')}
              >
                Join as Professional
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-accent text-accent hover:bg-accent hover:text-primary-foreground"
                onClick={() => navigate('/post-project')}
              >
                Create a Hiring Post
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;